import type { Card, Option, Cost, Requirements, ResourceType } from '../types';
import { GameState } from '../game/GameState';
import { Deck } from '../game/Deck';
import { checkCost, type CostCheckResult } from '../utils/CostChecker';
import { checkRequirements, type RequirementCheckResult } from '../utils/RequirementChecker';
import { getResourceIcon, getResourceLabel, getStatIcon } from '../utils/Icons';

export class Modal {
  private gameState: GameState;
  private deck: Deck;
  private onOptionSelect: (option: Option) => void;
  private onClose: () => void;
  private currentOverlay: HTMLElement | null = null;
  
  constructor(
    gameState: GameState,
    deck: Deck,
    onOptionSelect: (option: Option) => void,
    onClose: () => void
  ) {
    this.gameState = gameState;
    this.deck = deck;
    this.onOptionSelect = onOptionSelect;
    this.onClose = onClose;
  }
  
  private closeModal(): void {
    if (this.currentOverlay && this.currentOverlay.parentNode) {
      this.currentOverlay.parentNode.removeChild(this.currentOverlay);
      this.currentOverlay = null;
    }
    // Also try to remove any other modal overlays
    document.body.querySelectorAll('.modal-overlay').forEach(el => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  }
  
  show(card: Card): void {
    // Clean up any tooltips before showing modal
    document.querySelectorAll('.game-tooltip').forEach(tooltip => {
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    });
    
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    this.currentOverlay = overlay;
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.24s ease;
    `;
    
    // Add fade-in animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { transform: translateY(50px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    // Create modal content
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 16px;
      padding: 30px;
      max-width: 600px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      animation: slideUp 0.24s ease;
    `;
    
    // Card header
    const header = document.createElement('div');
    header.style.cssText = `
      margin-bottom: 20px;
      border-bottom: 2px solid rgba(255, 255, 255, 0.1);
      padding-bottom: 15px;
    `;
    
    const title = document.createElement('h2');
    title.style.cssText = `
      font-size: 24px;
      color: #fff;
      margin-bottom: 10px;
    `;
    title.textContent = card.name;
    
    const description = document.createElement('p');
    description.style.cssText = `
      font-size: 18px;
      color: #ccc;
      line-height: 1.6;
    `;
    description.textContent = card.description;
    
    header.appendChild(title);
    header.appendChild(description);
    
    // Options list
    const optionsList = document.createElement('div');
    optionsList.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 15px;
    `;
    
    card.options.forEach((option, index) => {
      const optionElement = this.createOptionElement(option, index === 0);
      optionsList.appendChild(optionElement);
    });
    
    // Close button
    const closeButton = document.createElement('button');
    closeButton.style.cssText = `
      margin-top: 20px;
      padding: 10px 20px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: #fff;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.2s ease;
    `;
    closeButton.textContent = 'Cancel';
    closeButton.addEventListener('mouseenter', () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.2)';
    });
    closeButton.addEventListener('mouseleave', () => {
      closeButton.style.background = 'rgba(255, 255, 255, 0.1)';
    });
    closeButton.addEventListener('click', () => {
      this.closeModal();
      this.onClose();
    });
    
    // Close on overlay click (outside modal)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.closeModal();
        this.onClose();
      }
    });
    
    modal.appendChild(header);
    modal.appendChild(optionsList);
    modal.appendChild(closeButton);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  }
  
  private createOptionElement(option: Option, isFirst: boolean): HTMLElement {
    const optionDiv = document.createElement('div');
    optionDiv.style.cssText = `
      background: rgba(0, 0, 0, 0.3);
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 20px;
      transition: all 0.2s ease;
    `;
    
    // Check if option is available
    const costCheck = checkCost(option.cost, this.gameState, this.deck);
    const reqCheck = checkRequirements(option.requirements, this.gameState);
    
    // Check if health would drop to 0 and sanity is 1 (would cause defeat)
    let wouldCauseHealthDefeat = false;
    if (option.effects) {
      for (const effect of option.effects) {
        if (effect && effect.health !== undefined && effect.health < 0) {
          const currentHealth = this.gameState.getResource('health');
          const currentSanity = this.gameState.getResource('sanity');
          const healthAfter = currentHealth + effect.health;
          if (healthAfter <= 0 && currentSanity === 1) {
            wouldCauseHealthDefeat = true;
            break;
          }
        }
      }
    }
    
    // First option is always free (no cost, but still needs to meet requirements)
    const canAfford = isFirst 
      ? reqCheck.meetsRequirements 
      : (costCheck.canAfford && reqCheck.meetsRequirements);
    
    // Check if sanity cost would cause defeat
    const currentSanity = this.gameState.getResource('sanity');
    const sanityAfterCost = costCheck.needsSanity ? currentSanity - costCheck.sanityCost : currentSanity;
    const wouldCauseSanityDefeat = sanityAfterCost <= 0;
    
    // Apply red background if would cause defeat
    if (wouldCauseSanityDefeat || wouldCauseHealthDefeat) {
      optionDiv.style.background = 'rgba(255, 74, 74, 0.2)';
      optionDiv.style.borderColor = 'rgba(255, 74, 74, 0.5)';
    }
    
    // Add visual indicator if Memory will be used for Tenacity
    if (reqCheck.needsMemory && reqCheck.memoryNeeded > 0) {
      const memoryIndicator = document.createElement('div');
      memoryIndicator.style.cssText = `
        position: absolute;
        top: 10px;
        right: 10px;
        background: rgba(255, 170, 0, 0.2);
        border: 1px solid rgba(255, 170, 0, 0.5);
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 15px;
        color: #ffaa00;
        font-weight: bold;
      `;
      memoryIndicator.textContent = `💭 Using ${reqCheck.memoryNeeded} Memory`;
      optionDiv.appendChild(memoryIndicator);
    }
    
    if (!canAfford) {
      optionDiv.style.opacity = '0.5';
      optionDiv.style.cursor = 'not-allowed';
      optionDiv.style.pointerEvents = 'none'; // Completely disable pointer events
      // Also add event listener as backup
      optionDiv.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }, true); // Use capture phase
    } else {
      optionDiv.style.pointerEvents = 'auto'; // Ensure pointer events are enabled
      optionDiv.style.cursor = 'pointer';
      optionDiv.addEventListener('mouseenter', () => {
        optionDiv.style.borderColor = 'rgba(255, 255, 255, 0.3)';
        optionDiv.style.background = wouldCauseSanityDefeat || wouldCauseHealthDefeat 
          ? 'rgba(255, 74, 74, 0.3)' 
          : 'rgba(0, 0, 0, 0.5)';
      });
      optionDiv.addEventListener('mouseleave', () => {
        optionDiv.style.borderColor = wouldCauseSanityDefeat || wouldCauseHealthDefeat
          ? 'rgba(255, 74, 74, 0.5)'
          : 'rgba(255, 255, 255, 0.1)';
        optionDiv.style.background = wouldCauseSanityDefeat || wouldCauseHealthDefeat
          ? 'rgba(255, 74, 74, 0.2)'
          : 'rgba(0, 0, 0, 0.3)';
      });
      optionDiv.addEventListener('click', (e) => {
        e.stopPropagation();
        // Close the modal
        this.closeModal();
        // Call the option select handler
        this.onOptionSelect(option);
      });
    }
    
    // Option name
    const nameElem = document.createElement('div');
    nameElem.style.cssText = `
      font-size: 18px;
      font-weight: bold;
      color: ${canAfford ? '#fff' : '#888'};
      margin-bottom: 15px;
      pointer-events: ${canAfford ? 'auto' : 'none'};
    `;
    nameElem.textContent = option.name;
    
    // Cost and requirements display
    const details = document.createElement('div');
    details.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 10px;
      font-size: 14px;
      pointer-events: ${canAfford ? 'auto' : 'none'};
    `;
    
    // Show cost breakdown
    if (option.cost && !isFirst) {
      const costElem = this.createCostDisplay(option.cost, costCheck);
      details.appendChild(costElem);
    }
    
    // Show requirements
    if (option.requirements) {
      const reqElem = this.createRequirementDisplay(option.requirements, reqCheck);
      details.appendChild(reqElem);
    }
    
    // Show if first option (free)
    if (isFirst) {
      const freeLabel = document.createElement('div');
      freeLabel.style.cssText = `
        color: #4aff9f;
        font-weight: bold;
        font-size: 14px;
      `;
      freeLabel.textContent = 'Free';
      details.appendChild(freeLabel);
    }
    
    // Show effects
    if (option.effects && option.effects.length > 0) {
      const effectsElem = this.createEffectsDisplay(option.effects);
      details.appendChild(effectsElem);
      
      // Check if inspiration is at max and this option gives inspiration
      const hasInspirationEffect = option.effects.some(effect => effect.unlock_inspiration);
      const currentInspiration = this.gameState.getInspirationsUnlocked();
      if (hasInspirationEffect && currentInspiration >= 3) {
        const warningElem = document.createElement('div');
        warningElem.style.cssText = `
          color: #ffaa00;
          font-weight: bold;
          font-size: 14px;
          margin-top: 10px;
          padding: 8px;
          background: rgba(255, 170, 0, 0.1);
          border: 1px solid rgba(255, 170, 0, 0.3);
          border-radius: 4px;
        `;
        warningElem.textContent = '⚠️ Warning: You are at maximum Inspiration (3/3). This effect will be wasted.';
        details.appendChild(warningElem);
      }
    }
    
    optionDiv.appendChild(nameElem);
    optionDiv.appendChild(details);
    
    return optionDiv;
  }
  
  private createCostDisplay(cost: Cost, _costCheck: CostCheckResult): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    `;
    
    const label = document.createElement('span');
    label.style.cssText = 'color: #888; margin-right: 4px;';
    label.textContent = 'Cost:';
    container.appendChild(label);
    
    const costParts: HTMLElement[] = [];
    
    // Resource costs
    if (cost.money) {
      costParts.push(this.createResourceCostElem('money', cost.money, this.gameState.getResource('money')));
    }
    if (cost.food) {
      costParts.push(this.createResourceCostElem('food', cost.food, this.gameState.getResource('food')));
    }
    if (cost.motivation) {
      costParts.push(this.createResourceCostElem('motivation', cost.motivation, this.gameState.getResource('motivation')));
    }
    if (cost.memory) {
      costParts.push(this.createResourceCostElem('memory', cost.memory, this.gameState.getResource('memory')));
    }
    if (cost.connections) {
      costParts.push(this.createResourceCostElem('connections', cost.connections, this.gameState.getResource('connections')));
    }
    if (cost.health) {
      const have = this.gameState.getResource('health');
      costParts.push(this.createResourceCostElem('health', cost.health, have, true));
    }
    if (cost.sanity) {
      const have = this.gameState.getResource('sanity');
      costParts.push(this.createResourceCostElem('sanity', cost.sanity, have, true));
    }
    if (cost.time) {
      // Note: Current card is already removed from deck by playCard(), so use deck size directly
      costParts.push(this.createTimeCostElem(cost.time, this.deck.getDeckSize()));
    }
    
    costParts.forEach((elem, index) => {
      if (index > 0) {
        const separator = document.createElement('span');
        separator.style.cssText = 'color: #888; margin: 0 4px 0 2px;';
        separator.textContent = ',';
        container.appendChild(separator);
      }
      container.appendChild(elem);
    });
    
    // Sanity warning - only show if Sanity cost would cause defeat
    if (cost.sanity) {
      const currentSanity = this.gameState.getResource('sanity');
      const sanityAfterCost = currentSanity - cost.sanity;
      const wouldCauseDefeat = sanityAfterCost <= 0;
      
      if (wouldCauseDefeat) {
        const sanityWarning = document.createElement('span');
        sanityWarning.style.cssText = `
          color: #ff4a4a;
          font-weight: bold;
          display: flex;
          align-items: center;
          gap: 5px;
          background: rgba(255, 74, 74, 0.2);
          padding: 4px 8px;
          border-radius: 4px;
        `;
        sanityWarning.innerHTML = '💀 -' + cost.sanity + ' Sanity (Defeat)';
        container.appendChild(sanityWarning);
      }
    }
    
    return container;
  }
  
  private createResourceCostElem(resource: string, needed: number, have: number, alwaysPayable: boolean = false): HTMLElement {
    const span = document.createElement('span');
    const canAfford = have >= needed;
    const resourceType = resource as ResourceType;
    const icon = getResourceIcon(resourceType);
    const label = getResourceLabel(resourceType);
    
    // Bad Habits is a negative resource, so colors are flipped
    // Paying bad_habit cost (losing bad habits) is good = green
    const isBadHabit = resource === 'bad_habit';
    let color = '#fff';
    if (alwaysPayable) {
      color = resource === 'health' ? '#ff4a4a' : '#aa4aff';
    } else if (isBadHabit) {
      color = canAfford ? '#4aff9f' : '#ff4a4a';
    } else {
      color = canAfford ? '#4aff9f' : '#ff4a4a';
    }
    
    span.style.cssText = `
      color: ${color};
      font-weight: ${canAfford || alwaysPayable ? 'normal' : 'bold'};
      display: inline-flex;
      align-items: center;
      gap: 4px;
    `;
    
    if (icon) {
      span.innerHTML = `<strong>${label}: -${needed}</strong> ${icon} (have: ${have}${alwaysPayable ? ', always payable' : ''})`;
    } else {
      span.textContent = `${label}: -${needed} (have: ${have}${alwaysPayable ? ', always payable' : ''})`;
    }
    
    return span;
  }
  
  private createTimeCostElem(needed: number, available: number): HTMLElement {
    const span = document.createElement('span');
    const canAfford = available >= needed;
    span.style.cssText = `
      color: ${canAfford ? '#4aff9f' : '#ff4a4a'};
      font-weight: ${canAfford ? 'normal' : 'bold'};
    `;
    span.textContent = `Time: ${needed} (available: ${available})`;
    return span;
  }
  
  private createRequirementDisplay(requirements: Requirements, _reqCheck: RequirementCheckResult): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    `;
    
    const label = document.createElement('span');
    label.style.cssText = 'color: #888; margin-right: 4px;';
    label.textContent = 'Requires:';
    container.appendChild(label);
    
    if (requirements.smarts) {
      const elem = this.createStatReqElem(getStatIcon('smarts'), 'smarts', requirements.smarts, this.gameState.getStat('smarts'));
      container.appendChild(elem);
    }
    if (requirements.tenacity) {
      const have = this.gameState.getStat('tenacity');
      const withMemory = have + this.gameState.getResource('memory');
      const needsMemory = have < requirements.tenacity && withMemory >= requirements.tenacity;
      const elem = this.createStatReqElem('👁️', 'tenacity', requirements.tenacity, have, needsMemory, withMemory);
      container.appendChild(elem);
    }
    if (requirements.social) {
      const elem = this.createStatReqElem('💬', 'social', requirements.social, this.gameState.getStat('social'));
      container.appendChild(elem);
    }
    
    return container;
  }
  
  private createStatReqElem(
    symbol: string, 
    stat: string, 
    needed: number, 
    have: number, 
    needsMemory?: boolean,
    withMemory?: number
  ): HTMLElement {
    const span = document.createElement('span');
    const meetsReq = needsMemory ? (withMemory !== undefined && withMemory >= needed) : have >= needed;
    
    if (needsMemory && withMemory !== undefined) {
      const memoryNeeded = withMemory - have;
      span.style.cssText = `
        color: #ffaa00;
        font-weight: bold;
        background: rgba(255, 170, 0, 0.15);
        padding: 4px 8px;
        border-radius: 4px;
        border: 1px solid rgba(255, 170, 0, 0.3);
        display: inline-block;
      `;
      span.innerHTML = `
        ${symbol} <strong>Tenacity: ${needed}</strong> 
        <span style="color: #ccc; font-size: 1em; display: block; margin-top: 2px;">(You have ${have}, using ${memoryNeeded} 💭 Memory to boost to ${withMemory})</span>
      `;
    } else {
      span.style.cssText = `
        color: ${meetsReq ? '#4aff9f' : '#ff4a4a'};
        font-weight: ${meetsReq ? 'normal' : 'bold'};
      `;
      span.textContent = `${symbol} ${stat}: ${needed} (have: ${have})`;
    }
    
    return span;
  }
  
  private createResourceEffectElem(resource: string, value: number): HTMLElement {
    const span = document.createElement('span');
    const resourceType = resource as ResourceType;
    const icon = getResourceIcon(resourceType);
    const label = getResourceLabel(resourceType);
    const isPositive = value > 0;
    const isNegative = value < 0;
    
    // Bad Habits is a negative resource, so colors are flipped
    const isBadHabit = resource === 'bad_habit';
    let color = '#fff';
    if (isBadHabit) {
      color = isPositive ? '#ff4a4a' : isNegative ? '#4aff9f' : '#fff';
    } else {
      color = isPositive ? '#4aff9f' : isNegative ? '#ff4a4a' : '#fff';
    }
    
    span.style.cssText = `
      color: ${color};
      display: inline-flex;
      align-items: center;
      gap: 4px;
    `;
    
    if (icon) {
      span.innerHTML = `<strong>${label}: ${value > 0 ? '+' : ''}${value}</strong> ${icon}`;
    } else {
      span.textContent = `${label}: ${value > 0 ? '+' : ''}${value}`;
    }
    
    return span;
  }
  
  private createEffectsDisplay(effects: any[]): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-top: 5px;
    `;
    
    const label = document.createElement('span');
    label.style.cssText = 'color: #888; font-weight: bold; margin-right: 4px;';
    label.textContent = 'Effects:';
    container.appendChild(label);
    
    const effectParts: HTMLElement[] = [];
    
    for (const effect of effects) {
      if (!effect || typeof effect !== 'object') continue;
      
      // Resource effects
      if (effect.money !== undefined) {
        effectParts.push(this.createResourceEffectElem('money', effect.money));
      }
      if (effect.food !== undefined) {
        effectParts.push(this.createResourceEffectElem('food', effect.food));
      }
      if (effect.motivation !== undefined) {
        effectParts.push(this.createResourceEffectElem('motivation', effect.motivation));
      }
      if (effect.memory !== undefined) {
        effectParts.push(this.createResourceEffectElem('memory', effect.memory));
      }
      if (effect.connections !== undefined) {
        effectParts.push(this.createResourceEffectElem('connections', effect.connections));
      }
      if (effect.health !== undefined) {
        effectParts.push(this.createResourceEffectElem('health', effect.health));
      }
      if (effect.bad_habit !== undefined) {
        effectParts.push(this.createResourceEffectElem('bad_habit', effect.bad_habit));
      }
      if (effect.sanity !== undefined) {
        effectParts.push(this.createResourceEffectElem('sanity', effect.sanity));
      }
      if (effect.dignity !== undefined) {
        effectParts.push(this.createResourceEffectElem('dignity', effect.dignity));
      }
      
      // Stat effects
      if (effect.level_up !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f;';
        span.textContent = `Choose stat to level up: +${effect.level_up}`;
        effectParts.push(span);
      }
      if (effect.level_up_tenacity !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f; display: inline-flex; align-items: center; gap: 4px;';
        span.innerHTML = `👁️ <strong>Tenacity: +${effect.level_up_tenacity}</strong>`;
        effectParts.push(span);
      }
      if (effect.level_up_smarts !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f; display: inline-flex; align-items: center; gap: 4px;';
        span.innerHTML = `${getStatIcon('smarts')} <strong>Smarts: +${effect.level_up_smarts}</strong>`;
        effectParts.push(span);
      }
      if (effect.level_up_social !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f; display: inline-flex; align-items: center; gap: 4px;';
        span.innerHTML = `💬 <strong>Social: +${effect.level_up_social}</strong>`;
        effectParts.push(span);
      }
      
      // Card action effects
      if (effect.exhaust_this_event) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f;';
        span.textContent = 'Exhaust this card';
        effectParts.push(span);
      }
      if (effect.create_opportunity) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f;';
        span.textContent = 'Create Opportunity';
        effectParts.push(span);
      }
      if (effect.unlock_inspiration) {
        const span = document.createElement('span');
        span.style.cssText = `
          color: #4aff9f;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        `;
        span.innerHTML = `💡 <strong>Inspiration: +1</strong>`;
        effectParts.push(span);
      }
      if (effect.thinking_modal) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f;';
        span.textContent = 'Thinking Modal';
        effectParts.push(span);
      }
    }
    
    if (effectParts.length > 0) {
      effectParts.forEach((elem, index) => {
        if (index > 0) {
          const separator = document.createElement('span');
          separator.style.cssText = 'color: #888; margin: 0 4px 0 2px;';
          separator.textContent = ',';
          container.appendChild(separator);
        }
        container.appendChild(elem);
      });
    }
    
    return container;
  }
}

